/**
 * Create Promo - Main Stepper Container
 * 
 * 6-Step promo creation flow:
 * Step 1: Basic Information
 * Step 2: Discount Configuration
 * Step 3: Targeting
 * Step 4: Eligibility & Conditions
 * Step 5: Stackability Rules
 * Step 6: Summary
 */

import React, { useEffect, useState, useMemo } from 'react';
import { Container, Row, Col, Card, Alert } from 'react-bootstrap';
import { useNavigate, useLocation, useParams } from 'react-router-dom';

// Common components
import Breadcrumbs from '../../../../Common/Breadcrumbs';
import { SuccessModal } from '../../../../Common/SuccessModal';
import { DeleteModal } from '../../../../Common/DeleteModal';
import '../../Campaigns/CreateCampaign/CreateCampaign.css'; // Reuse campaign styles

// Promo creation components
import PromoStepper from './components/PromoStepper';
// Step components will be created next
import StepBasicInfo from './components/StepBasicInfo';
import StepDiscountConfig from './components/StepDiscountConfig';
import StepTargeting from './components/StepTargeting';
import StepEligibility from './components/StepEligibility';
import StepStackability from './components/StepStackability';
import StepSummary from './components/StepSummary';
import PromoPreview from './components/PromoPreview';

// Custom hooks and types
import { usePromoForm } from './hooks/usePromoForm';
import { 
  PromoFormData, 
  PromoStatus, 
  Promo,
  PromoDomain,
  TransactionType,
  UserType,
  BenefitType,
  BenefitLevel,
  DiscountMode,
  EntityTargetingMode,
  PromoVisibilityType,
} from '../../../../api/promos/types';
import { getPromoById, updatePromoStatus, updatePromo, deletePromo } from '../../../../api/promos';
import { CatalogueConfigProvider } from '../../../../contexts/CatalogueConfigContext';

const CreatePromoContent: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  
  // Check if we're in edit mode
  const isEditMode = location.pathname.includes('/edit');
  const isViewMode = id && !isEditMode;
  
  // Check if we're duplicating an existing promo
  const duplicateFrom = location.state?.duplicateFrom;
  
  // State for loading promo data
  const [loadingPromo, setLoadingPromo] = useState(isEditMode || isViewMode);
  
  // Store original promo data for change detection
  const [originalPromoData, setOriginalPromoData] = useState<PromoFormData | null>(null);
  const [originalStatus, setOriginalStatus] = useState<PromoStatus | null>(null);
  const [promoName, setPromoName] = useState<string>('');
  const [hasNavigatedToSummary, setHasNavigatedToSummary] = useState(false);
  
  // Success modal state
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successTitle, setSuccessTitle] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  
  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Initialize form data
  const [initialFormData, setInitialFormData] = useState<PromoFormData>(() => {
    if (duplicateFrom) {
      return {
        name: `${duplicateFrom.name} (Copy)`,
        code: duplicateFrom.code,
        listingTitle: duplicateFrom.listingTitle ?? '',
        appliedTitle: duplicateFrom.appliedTitle ?? '',
        campaignId: duplicateFrom.campaignId,
        domain: duplicateFrom.domain,
        transactionType: duplicateFrom.transactionType,
        discountMode: duplicateFrom.discountMode || DiscountMode.CODE_BASED,
        eligibility: duplicateFrom.eligibility,
        benefit: duplicateFrom.benefit,
        scope: duplicateFrom.scope,
        usageLimits: duplicateFrom.usageLimits,
        stackingRules: {
          allowWithReferral: false, // Always false
          allowWithOtherPromos: false, // Always false
          exclusive: duplicateFrom.stackingRules?.exclusive || false,
          maxTotalDiscountPercent: duplicateFrom.stackingRules?.maxTotalDiscountPercent,
        },
        conditions: {
          ...duplicateFrom.conditions,
          fullPaymentRequired: true, // Always true
        },
        funding: duplicateFrom.funding || {
          fundedBy: 'PLATFORM',
          sharePercentage: 100,
        },
        visibility: duplicateFrom.visibility || {
          type: PromoVisibilityType.PUBLIC,
        },
        validity: {
          startAt: '', // User must fill dates
          endAt: '', // User must fill dates
        },
      };
    }
    return {
      name: '',
      code: '',
      listingTitle: '',
      appliedTitle: '',
      domain: PromoDomain.SHOP,
      transactionType: TransactionType.SHOP_PURCHASE,
      discountMode: DiscountMode.AUTOMATIC,
      eligibility: {
        userType: UserType.ALL,
      },
      benefit: {
        type: BenefitType.PERCENTAGE_DISCOUNT,
        level: BenefitLevel.ITEM,
        value: 0,
        maxDiscountAmount: undefined,
      },
      validity: {
        startAt: '',
        endAt: '',
      },
      stackingRules: {
        allowWithReferral: false, // Always false
        allowWithOtherPromos: false, // Always false
        exclusive: false,
        maxTotalDiscountPercent: undefined,
      },
      conditions: {
        fullPaymentRequired: true, // Always true
      },
      funding: {
        fundedBy: 'PLATFORM',
        sharePercentage: 100,
      },
      visibility: {
        type: PromoVisibilityType.PUBLIC,
      },
    };
  });
  
  // Fetch promo data if in edit/view mode
  useEffect(() => {
    const fetchPromoData = async () => {
      if (id && (isEditMode || isViewMode)) {
        try {
          setLoadingPromo(true);
          const promo = await getPromoById(id);
          
          // Format dates for input fields (YYYY-MM-DD)
          const formatDateForInput = (dateString: string) => {
            if (!dateString) return '';
            try {
              const date = new Date(dateString);
              return date.toISOString().split('T')[0];
            } catch {
              return '';
            }
          };
          
          const formattedData: PromoFormData = {
            name: promo.name,
            code: promo.code,
            listingTitle: promo.listingTitle ?? '',
            appliedTitle: promo.appliedTitle ?? '',
            campaignId: promo.campaignId,
            domain: promo.domain,
            transactionType: promo.transactionType,
            discountMode: promo.discountMode || DiscountMode.CODE_BASED,
            eligibility: promo.eligibility,
            benefit: promo.benefit,
            scope: promo.scope,
            usageLimits: promo.usageLimits,
            stackingRules: promo.stackingRules || {
              allowWithReferral: false,
              allowWithOtherPromos: false,
              exclusive: false,
            },
            conditions: promo.conditions,
            funding: promo.funding || {
              fundedBy: 'PLATFORM',
              sharePercentage: 100,
            },
            visibility: promo.visibility || {
              type: PromoVisibilityType.PUBLIC,
            },
            validity: {
              startAt: formatDateForInput(promo.validity.startAt),
              endAt: formatDateForInput(promo.validity.endAt),
            },
          };
          
          setInitialFormData(formattedData);
          setOriginalPromoData(formattedData);
          setOriginalStatus(promo.status);
          setPromoName(promo.name);
        } catch (error: any) {
          const errorMessage = error?.message || 'Failed to load promo data.';
          setError(errorMessage);
          setTimeout(() => navigate('/promos/promos'), 2000);
        } finally {
          setLoadingPromo(false);
        }
      }
    };
    
    fetchPromoData();
  }, [id, isEditMode, isViewMode, navigate]);
  
  // Use promo form hook
  const {
    currentStep,
    formData,
    validationErrors,
    isSubmitting,
    steps,
    goToStep,
    validateStep,
    handleFormChange,
    saveDraft,
    submitPromo,
  } = usePromoForm(initialFormData, id, isEditMode);
  
  // Reset navigation flag when promo ID changes
  useEffect(() => {
    setHasNavigatedToSummary(false);
  }, [id]);
  
  // Navigate to summary page (step 5) when in edit/view mode and data is loaded
  useEffect(() => {
    if ((isEditMode || isViewMode) && !loadingPromo && originalPromoData && !hasNavigatedToSummary && currentStep === 0) {
      setHasNavigatedToSummary(true);
      goToStep(5); // Navigate to Summary step
    }
  }, [isEditMode, isViewMode, loadingPromo, originalPromoData, hasNavigatedToSummary, currentStep, goToStep]);
  
  // Detect if form data has changed (for edit mode)
  const hasChanges = useMemo(() => {
    if (!isEditMode || !originalPromoData) {
      return false;
    }
    
    return (
      formData.name !== originalPromoData.name ||
      formData.code !== originalPromoData.code ||
      formData.listingTitle !== originalPromoData.listingTitle ||
      formData.appliedTitle !== originalPromoData.appliedTitle ||
      formData.campaignId !== originalPromoData.campaignId ||
      formData.domain !== originalPromoData.domain ||
      formData.transactionType !== originalPromoData.transactionType ||
      formData.discountMode !== originalPromoData.discountMode ||
      JSON.stringify(formData.benefit) !== JSON.stringify(originalPromoData.benefit) ||
      JSON.stringify(formData.eligibility) !== JSON.stringify(originalPromoData.eligibility) ||
      JSON.stringify(formData.scope) !== JSON.stringify(originalPromoData.scope) ||
      JSON.stringify(formData.usageLimits) !== JSON.stringify(originalPromoData.usageLimits) ||
      JSON.stringify(formData.stackingRules) !== JSON.stringify(originalPromoData.stackingRules) ||
      JSON.stringify(formData.conditions) !== JSON.stringify(originalPromoData.conditions) ||
      JSON.stringify(formData.validity) !== JSON.stringify(originalPromoData.validity) ||
      JSON.stringify(formData.funding) !== JSON.stringify(originalPromoData.funding) ||
      JSON.stringify(formData.visibility) !== JSON.stringify(originalPromoData.visibility)
    );
  }, [formData, originalPromoData, isEditMode]);
  
  // Handle back navigation
  const handleBack = () => {
    // Check if user came from detail page
    const fromDetail = location.state?.fromDetail;
    
    if (fromDetail && id) {
      // Navigate back to detail page
      navigate(`/promos/promos/${id}/detail`);
    } else {
      // Navigate to listing page (backward compatibility)
      navigate('/promos/promos');
    }
  };
  
  // Handle step navigation
  const handleStepClick = (stepIndex: number) => {
    if (stepIndex < currentStep || validateStep(currentStep)) {
      goToStep(stepIndex);
    }
  };
  
  // Handle next step
  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep < steps.length - 1) {
        goToStep(currentStep + 1);
      }
    }
  };
  
  // Handle previous step
  const handlePrevious = () => {
    if (currentStep > 0) {
      goToStep(currentStep - 1);
    }
  };
  
  // Handle save as draft
  const handleSaveDraft = async () => {
    if (!id && !isEditMode) {
      try {
        await saveDraft();
        setSuccessTitle('Promo Saved as Draft');
        setSuccessMessage(`Promo "${formData.name}" saved as draft successfully.`);
        setShowSuccessModal(true);
      } catch (error: any) {
        const errorMessage = error?.message || 'Failed to save promo as draft.';
        setError(errorMessage);
      }
      return;
    }

    if (!id) return;
    
    try {
      if (hasChanges) {
        await updatePromo(id, formData, originalPromoData || undefined);
      }
      
      if (originalStatus !== PromoStatus.DRAFT) {
        await updatePromoStatus(id, PromoStatus.DRAFT);
      }
      
      const action = hasChanges ? 'updated' : 'saved';
      setSuccessTitle('Promo Saved as Draft');
      setSuccessMessage(`Promo "${formData.name}" ${action} as draft successfully.`);
      setShowSuccessModal(true);
    } catch (error: any) {
      const errorMessage = error?.message || 'Failed to save promo as draft.';
      setError(errorMessage);
    }
  };
  
  // Handle activate/update promo
  const handleActivate = async () => {
    if (!id && !isEditMode) {
      try {
        await submitPromo();
        setSuccessTitle('Promo Activated');
        setSuccessMessage(`Promo "${formData.name}" created and activated successfully.`);
        setShowSuccessModal(true);
      } catch (error: any) {
        const errorMessage = error?.message || 'Failed to create and activate promo.';
        setError(errorMessage);
      }
      return;
    }

    if (!id) return;
    
    try {
      if (hasChanges) {
        await updatePromo(id, formData, originalPromoData || undefined);
      }
      
      if (originalStatus !== PromoStatus.ACTIVE) {
        await updatePromoStatus(id, PromoStatus.ACTIVE);
      }
      
      const action = hasChanges ? 'updated and activated' : 'activated';
      setSuccessTitle('Promo Activated');
      setSuccessMessage(`Promo "${formData.name}" ${action} successfully.`);
      setShowSuccessModal(true);
    } catch (error: any) {
      const errorMessage = error?.message || 'Failed to activate promo.';
      setError(errorMessage);
    }
  };

  // Handle deactivate promo
  const handleDeactivate = async () => {
    if (!id) return;
    
    try {
      await updatePromoStatus(id, PromoStatus.INACTIVE);
      setSuccessTitle('Promo Deactivated');
      setSuccessMessage(`Promo "${formData.name}" deactivated successfully.`);
      setShowSuccessModal(true);
    } catch (error: any) {
      const errorMessage = error?.message || 'Failed to deactivate promo.';
      setError(errorMessage);
    }
  };

  // Handle delete promo - show modal
  const handleDelete = async () => {
    setShowDeleteModal(true);
  };
  
  // Confirm delete promo
  const confirmDeletePromo = async () => {
    if (!id) return;
    
    try {
      setIsDeleting(true);
      await deletePromo(id);
      setShowDeleteModal(false);
      setSuccessTitle('Promo Deleted');
      setSuccessMessage(`Promo "${formData.name}" deleted successfully.`);
      setShowSuccessModal(true);
    } catch (error: any) {
      const errorMessage = error?.message || 'Failed to delete promo.';
      setError(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };
  
  // Set page title
  useEffect(() => {
    if (isEditMode) {
      document.title = `Edit Promo | SFL Admin`;
    } else if (isViewMode) {
      document.title = `View Promo | SFL Admin`;
    } else if (duplicateFrom) {
      document.title = `Duplicate Promo - ${duplicateFrom.name} | SFL Admin`;
    } else {
      document.title = 'Create Promo | SFL Admin';
    }
  }, [isEditMode, isViewMode, duplicateFrom]);
  
  // Show loading state while fetching promo data
  if (loadingPromo) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
            <div className="text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3">Loading promo data...</p>
            </div>
          </div>
        </Container>
      </div>
    );
  }
  
  return (
    <div className="page-content">
      <Container fluid>
        {/* Breadcrumbs */}
        <div className="mb-3" style={{ padding: '0 15px' }}>
          <Breadcrumbs 
            items={[
              { label: 'Promos', onClick: () => navigate('/promos') },
              { label: 'All Promos', onClick: () => navigate('/promos/promos') },
              { 
                label: isEditMode 
                  ? 'Edit Promo' 
                  : isViewMode 
                    ? 'View Promo'
                    : duplicateFrom 
                      ? `Duplicate - ${duplicateFrom.name}` 
                      : 'Create Promo', 
                active: true 
              }
            ]} 
          />
        </div>
        
        {/* Header */}
        <div className="campaign-detail-header">
          <div className="campaign-detail-header-content">
            <button
              className="campaign-detail-back-button"
              onClick={handleBack}
              aria-label="Go back"
            >
              <i className="ph-arrow-left"></i>
            </button>
            <div className="campaign-detail-title-section">
              <h1 className="campaign-detail-title">
                {isEditMode 
                  ? (promoName || 'Edit Promo')
                  : isViewMode 
                    ? (promoName || 'View Promo')
                    : duplicateFrom 
                      ? `Duplicate Promo` 
                      : 'Create Promo'}
              </h1>
              <p className="campaign-detail-subtitle">
                Step {currentStep + 1} of {steps.length}
              </p>
            </div>
          </div>
        </div>
        
        {/* Stepper */}
        <div className="mb-4">
          <PromoStepper
            steps={steps}
            currentStep={currentStep}
            onStepClick={handleStepClick}
          />
        </div>
        
        {/* Main Content */}
        <Row>
          {/* Form Section */}
          <Col lg={currentStep === 5 ? 12 : 8}>
            {currentStep === 5 ? (
              /* Step 6 (Summary) - No Card wrapper, transparent background */
              <div className="p-4">
                <StepSummary
                  formData={formData}
                  validationErrors={validationErrors}
                  isSubmitting={isSubmitting}
                  onPrevious={handlePrevious}
                  onSaveDraft={handleSaveDraft}
                  onActivate={handleActivate}
                  onDeactivate={handleDeactivate}
                  onDelete={handleDelete}
                  onEditStep={handleStepClick}
                  isEditMode={isEditMode}
                  hasChanges={hasChanges}
                  originalStatus={originalStatus}
                />
              </div>
            ) : (
              <Card>
                <Card.Body className="p-4">
                  {/* Step Content */}
                  {currentStep === 0 && (
                    <StepBasicInfo
                      formData={formData}
                      validationErrors={validationErrors}
                      onChange={handleFormChange}
                      onNext={handleNext}
                      onCancel={handleBack}
                    />
                  )}
                  
                  {currentStep === 1 && (
                    <StepDiscountConfig
                      formData={formData}
                      validationErrors={validationErrors}
                      onChange={handleFormChange}
                      onNext={handleNext}
                      onPrevious={handlePrevious}
                    />
                  )}
                  
                  {currentStep === 2 && (
                    <StepTargeting
                      formData={formData}
                      validationErrors={validationErrors}
                      onChange={handleFormChange}
                      onNext={handleNext}
                      onPrevious={handlePrevious}
                    />
                  )}
                  
                  {currentStep === 3 && (
                    <StepEligibility
                      formData={formData}
                      validationErrors={validationErrors}
                      onChange={handleFormChange}
                      onNext={handleNext}
                      onPrevious={handlePrevious}
                    />
                  )}
                  
                  {currentStep === 4 && (
                    <StepStackability
                      formData={formData}
                      validationErrors={validationErrors}
                      onChange={handleFormChange}
                      onNext={handleNext}
                      onPrevious={handlePrevious}
                    />
                  )}
                </Card.Body>
              </Card>
            )}
          </Col>
          
          {/* Preview Section - Hidden on Step 5 (Summary) */}
          {currentStep !== 5 && (
            <Col lg={4}>
              <div className="position-sticky" style={{ top: '100px' }}>
                <PromoPreview
                  formData={formData}
                  currentStep={currentStep}
                />
              </div>
            </Col>
          )}
        </Row>
        
        {/* Success Modal */}
        <SuccessModal
          show={showSuccessModal}
          handleClose={() => {
            setShowSuccessModal(false);
            navigate('/promos/promos');
          }}
          title={successTitle}
          message={successMessage}
        />
        
        {/* Delete Modal */}
        <DeleteModal
          show={showDeleteModal}
          handleClose={() => setShowDeleteModal(false)}
          deleteModalFunction={confirmDeletePromo}
          title="Delete Promo"
          message={`Are you sure you want to delete "${formData.name}"? This action cannot be undone.`}
          isLoading={isDeleting}
        />
      </Container>
    </div>
  );
};

// Main component with context provider
const CreatePromo: React.FC = () => {
  return (
    <CatalogueConfigProvider>
      <CreatePromoContent />
    </CatalogueConfigProvider>
  );
};

export default CreatePromo;
